'use client';

import { EmblaOptionsType, EmblaCarouselType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import React, { useEffect, useCallback } from 'react';

import {
  PrevButton,
  NextButton,
  usePrevNextButtons
} from '../components/Arrows/Arrows';
import { DotButton, useDotButton } from '../components/Dot/Dot';
import { useNestedCarousel } from '../hooks';

import { styles } from './UiKitCarouselBase.styles';

export interface UiKitCarouselBaseProps {
  className?: string;
  autoplay?: boolean | number;
  fade?: boolean;
  showArrows?: boolean;
  showDots?: boolean;
  children?: React.ReactNode;
  dotsClass?: string;
  arrowPrevClass?: string;
  arrowNextClass?: string;
  arrowType?: 'arrow' | 'chevron' | 'arrow-short';
  options?: EmblaOptionsType;
  onSlidesChange?: (index: number) => void;
  loop?: boolean;
  viewportClassName?: string;
  isNested?: boolean;
  dragFree?: boolean;
  renderButtons?: (buttons: React.ReactNode) => React.ReactNode;
  renderCustomControls?: (
    controls: ReturnType<typeof usePrevNextButtons>
  ) => React.ReactNode;
  activeDotClass?: string;
  withScrollGestures?: boolean;
  onInit?: (api: EmblaCarouselType) => void;
  skipSnaps?: boolean;
  slidesToScroll?: number;
  alwaysShowArrows?: boolean;
}

export function UiKitCarouselBase({
  className,
  autoplay = false,
  fade = false,
  showArrows = true,
  showDots = false,
  dotsClass,
  arrowPrevClass,
  arrowNextClass,
  arrowType,
  children,
  options,
  onSlidesChange,
  loop = true,
  viewportClassName,
  isNested = false,
  dragFree = false,
  withScrollGestures = false,
  renderButtons,
  activeDotClass,
  renderCustomControls,
  onInit,
  skipSnaps = false,
  slidesToScroll = 1,
  alwaysShowArrows = false
}: UiKitCarouselBaseProps) {
  const slots = styles({});

  const { watchDrag, setupNestedHandlers } = useNestedCarousel(isNested);

  const defaultOptions: EmblaOptionsType = {
    loop,
    align: 'start',
    watchDrag,
    dragFree: dragFree,
    skipSnaps,
    slidesToScroll
  };

  const combinedOptions: EmblaOptionsType = { ...defaultOptions, ...options };

  const plugins = [];

  if (autoplay) {
    const delay = autoplay === true ? 5000 : Number(autoplay);
    plugins.push(Autoplay({ delay }));
  }
  if (fade) {
    plugins.push(Fade());
  }
  if (withScrollGestures) {
    plugins.push(WheelGesturesPlugin());
  }

  const [emblaRef, emblaApi] = useEmblaCarousel(combinedOptions, plugins);

  useEffect(() => {
    if (!emblaApi) return;
    onInit?.(emblaApi);
    return setupNestedHandlers(emblaApi);
  }, [emblaApi, setupNestedHandlers, onInit]);

  const onNavButtonClick = useCallback(() => {
    if (emblaApi && emblaApi?.plugins()?.autoplay) {
      emblaApi.plugins()?.autoplay?.reset();
    }
  }, [emblaApi]);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi,
    onNavButtonClick
  );

  const controls = usePrevNextButtons(emblaApi, onNavButtonClick);

  const controlsVisible =
    options?.active || !controls.prevBtnDisabled || !controls.nextBtnDisabled;

  const shouldShowArrows =
    !alwaysShowArrows || !controls.prevBtnDisabled || !controls.nextBtnDisabled;

  useEffect(() => {
    if (onSlidesChange) {
      onSlidesChange(selectedIndex);
    }
  }, [selectedIndex, onSlidesChange]);

  const buttonsNode = (
    <>
      {showArrows &&
        shouldShowArrows &&
        (alwaysShowArrows || !controls.prevBtnDisabled) && (
          <PrevButton
            className={slots.arrow({
              disabled: controls.prevBtnDisabled,
              className: arrowPrevClass
            })}
            onClick={controls.onPrevButtonClick}
            arrowType={arrowType}
            disabled={controls.prevBtnDisabled}
          />
        )}

      {showArrows &&
        shouldShowArrows &&
        (alwaysShowArrows || !controls.nextBtnDisabled) && (
          <NextButton
            className={slots.arrow({
              disabled: controls.nextBtnDisabled,
              className: arrowNextClass
            })}
            onClick={controls.onNextButtonClick}
            arrowType={arrowType}
            disabled={controls.nextBtnDisabled}
          />
        )}
    </>
  );

  return (
    <div className={slots.base({ className })}>
      <div
        className={slots.viewport({ className: viewportClassName })}
        ref={emblaRef}
      >
        <div className={slots.container()}>
          {React.Children.toArray(children).map((child) =>
            React.isValidElement(child) ? (
              <div
                key={child.key}
                className={slots.slide()}
              >
                {child}
              </div>
            ) : null
          )}
        </div>
      </div>

      {renderButtons?.(buttonsNode) ?? buttonsNode}
      {renderCustomControls?.(controls)}

      {controlsVisible && showDots && (
        <div className={slots.dots({ className: dotsClass })}>
          {scrollSnaps.length > 1 &&
            scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={slots.dot({
                  active: index === selectedIndex,
                  className: index === selectedIndex ? activeDotClass : ''
                })}
              />
            ))}
        </div>
      )}
    </div>
  );
}
